import { Cake as CakeIcon, Person as PersonIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ViewComfyOutlinedIcon from '@mui/icons-material/ViewComfyOutlined';
import { Chip, Grid, Stack, TableCell, TableRow, Tooltip, Typography, MenuItem, IconButton } from '@mui/material';
import { PatientManager } from 'src/@types/crs/patient';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import MedicaIdIcon from '../../../../assets/icons/medicaid.svg';
import MRNIcon from '../../../../assets/icons/mrn.svg';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import CustomLink from 'src/careflow/common/CustomLink';
import uuidv4 from 'src/utils/uuidv4';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { useNavigate } from 'react-router-dom';
import { Menu } from '@mui/material';

type Props = {
  row: PatientManager;
  onDeactivate?: (patientId: string) => void;
  onActivate?: (patientId: string) => void;
  isDeactivating?: boolean;
  isActivating?: boolean;
};

export default function PatientRow({ row, onDeactivate, onActivate, isDeactivating = false, isActivating = false }: Props) {
  const {
    id,
    mrn,
    referral,
    phone,
    email,
    gender,
    formattedBirthDate,
    age,
    medicaId,
    sil,
    createdAt,
    lastUpdated,
    status,
    episodeOfCareData,
    identifiers,
    managingOrg
  } = row;

  const { componentsData } = useTenantConfigData();
  const { showSILInformation, showMedicaID } = componentsData ?? {};
  const { i18n } = useLocales();
  const navigate = useNavigate();

  const [openMenu, setOpenMenu] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleDeactivate = () => {
    if (onDeactivate) {
      onDeactivate(id);
    }
    handleCloseMenu();
  };

  const handleActivate = () => {
    if (onActivate) {
      onActivate(id);
    }
    handleCloseMenu();
  };

  const handleViewDetails = () => {
    navigate(PATH_DASHBOARD.crs.patient.details.forId(row.id));
    handleCloseMenu();
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Tooltip title={i18n('patients.mrn', 'crs')}>
            <Typography variant="subtitle2">
              <MRNIcon style={{ height: 13, width: 13, marginRight: 9 }} />
              <CustomLink to={PATH_DASHBOARD.crs.patient.details.forId(row.id)}>{mrn}</CustomLink>
            </Typography>
          </Tooltip>
          {identifiers?.map((i) => (
            <Tooltip title={i?.tooltip} key={uuidv4()}>
              <Typography variant="body2" display="flex" alignItems="center">
                <Icon 
                  icon={i?.icon}
                  style={{ ...i?.properties }}
                />
                {i?.value ? i?.value : null}
              </Typography>
            </Tooltip>
          ))}
        </TableCell>
        <TableCell>
          <Typography variant="subtitle2">
            <PersonIcon sx={{ marginRight: 1, marginBottom: '-2px' }} fontSize={'small'} />{' '}
            <CustomLink to={PATH_DASHBOARD.crs.patient.details.forId(row.id)}>
              {referral}
            </CustomLink>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <CakeIcon
              sx={{ marginRight: 1, color: '#919eab', marginBottom: '-2px' }}
              fontSize={'small'}
            />
            {formattedBirthDate ? `${formattedBirthDate} (${age})` : null}
          </Typography>
          <Typography variant="body2">
            {gender && `${gender?.charAt(0).toUpperCase()}${gender?.slice(1)}`}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            <PhoneIcon
              sx={{ marginRight: 1, color: '#919eab', marginBottom: '-2px' }}
              fontSize={'small'}
            />
            {phone}
          </Typography>
          <Typography variant="body2">
            <EmailIcon
              sx={{ marginRight: 1, color: '#919eab', marginBottom: '-5px' }}
              fontSize={'small'}
            />
            {email}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip 
            size="small" 
            label={status} 
            color={(status === 'Active' && 'success') || 'error'} 
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5,
              }
            }}
          />
        </TableCell>

        <TableCell>
          <Grid container direction="column" spacing={1}>
            {episodeOfCareData &&
              Object.keys(episodeOfCareData).map((eocId) => {
                const eoc = episodeOfCareData[eocId];
                const title = eoc.type[0].text;
                let firstTypeCodingDisplay: string | undefined = undefined;
                eoc.type.find((item) => {
                  const firstCodingDisplay = item.coding.find((cod) => cod.display);

                  if (firstCodingDisplay) {
                    firstTypeCodingDisplay = firstCodingDisplay.display;
                  }

                  return !!firstCodingDisplay;
                })
                let label = '';

                if (title) {
                  label += title + ' : ';
                } else if (firstTypeCodingDisplay) {
                  label += firstTypeCodingDisplay + ' : ';
                } else {
                  label += '- : ';
                }

                label += eoc.status;

                return (
                  <Grid key={eocId} item xs={12}>
                    <Chip key={eocId} size="small" label={label} color={'info'} />
                  </Grid>
                );
              })}
          </Grid>
        </TableCell>

        {!!showSILInformation && (
          <TableCell>
            {!!sil && (
              <Stack direction='row'>
                <ViewComfyOutlinedIcon
                  sx={{ marginRight: 1, color: '#919eab' }}
                  fontSize={'small'}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  SIL Score: {sil ? sil : null}
                </Typography>
              </Stack>
            )}
          </TableCell>
        )}
        <TableCell>
          <Typography variant="body2">{managingOrg}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">Registration Date: {createdAt ? createdAt : null}</Typography>
          <Typography variant="body2">Last Updated: {lastUpdated ? lastUpdated : '-'}</Typography>
        </TableCell>
        <TableCell align="right">
          <IconButton 
            onClick={handleOpenMenu}
            sx={{ 
              color: '#637381',
              '&:hover': {
                backgroundColor: 'rgba(99, 115, 129, 0.08)',
              }
            }}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
          
          <Menu
            anchorEl={openMenu}
            open={Boolean(openMenu)}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                borderRadius: 1,
                boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 20px -4px rgba(145, 158, 171, 0.12)',
                minWidth: 140,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  typography: 'body2',
                  borderRadius: 0.75,
                },
              },
            }}
          >
            {status === 'Active' && (
              <MenuItem
                onClick={handleDeactivate}
                disabled={isDeactivating}
                sx={{ 
                  color: isDeactivating ? 'text.disabled' : 'error.main',
                  '&:hover': {
                    backgroundColor: isDeactivating ? 'transparent' : 'rgba(255, 86, 48, 0.08)',
                  }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  {isDeactivating ? (
                    <Iconify icon="eos-icons:loading" sx={{ fontSize: 20 }} />
                  ) : (
                    <Iconify icon="eva:trash-2-outline" sx={{ fontSize: 20 }} />
                  )}
                  <Typography variant="body2">
                    {isDeactivating ? 'Deactivating...' : 'Deactivate'}
                  </Typography>
                </Stack>
              </MenuItem>
            )}
            {status === 'Inactive' && (
              <MenuItem
                onClick={handleActivate}
                disabled={isActivating}
                sx={{ 
                  color: isActivating ? 'text.disabled' : 'success.main',
                  '&:hover': {
                    backgroundColor: isActivating ? 'transparent' : 'rgba(34, 197, 94, 0.08)',
                  }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  {isActivating ? (
                    <Iconify icon="eos-icons:loading" sx={{ fontSize: 20 }} />
                  ) : (
                    <Iconify icon="eva:checkmark-circle-2-outline" sx={{ fontSize: 20 }} />
                  )}
                  <Typography variant="body2">
                    {isActivating ? 'Activating...' : 'Activate'}
                  </Typography>
                </Stack>
              </MenuItem>
            )}
            <MenuItem
              onClick={handleViewDetails}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(99, 115, 129, 0.08)',
                }
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <OpenInNewIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2">Details</Typography>
              </Stack>
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
    </>
  );
}
